/**
 * Pure helpers for the grouped carrier-delay feed card. A `carrier_delay` root
 * ("MSC delayed — N bookings across V vessels") expands to `vessel_delay`
 * effects; each effect carries the bookings on that vessel with their delay
 * days. These helpers parse and derive the data the `EffectRow` renders, kept
 * separate from the component so they can be unit-tested without a DOM.
 */
import type { VesselDelayBooking, VesselDelayPayload } from "@/types/feed";

/** Short, human label for the ETD delay leg attribution carried per booking. */
export const ETD_DELAY_LEG_LABELS: Record<
  NonNullable<VesselDelayBooking["etd_delay_leg"]>,
  string
> = {
  feeder_barge: "Feeder/barge",
  mother_vessel: "Mother vessel",
  unknown: "Unknown",
};

/** Resolve the display label for a booking's ETD delay leg, or null if absent. */
export function vesselDelayLegLabel(booking: VesselDelayBooking): string | null {
  const leg = booking.etd_delay_leg;
  if (!leg) return null;
  return ETD_DELAY_LEG_LABELS[leg] ?? null;
}

/** Whether an effect payload is a grouped vessel-delay breakdown. */
export function isVesselDelayPayload(
  payload: unknown,
): payload is VesselDelayPayload {
  if (!payload || typeof payload !== "object") return false;
  const p = payload as Record<string, unknown>;
  return p.kind === "vessel_delay" && Array.isArray(p.bookings);
}

/**
 * The distinct live tracking vessels the cargo actually moved onto, in
 * first-seen order. Empty when no booking diverged from the planned vessel.
 */
export function vesselDelayLiveVessels(payload: VesselDelayPayload): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const booking of payload.bookings ?? []) {
    const vessel = booking.operational_vessel?.trim();
    if (vessel && !seen.has(vessel)) {
      seen.add(vessel);
      out.push(vessel);
    }
  }
  return out;
}
